import React, { useState } from 'react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { Button, Badge, Dropdown, DropdownContextMenu } from '@ygtechnologies/rubus-ui';
import { LoadingIndicator, SimpleCard } from '@ygtechnologies/bygr-frontend-component-library';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faPlus, faCube, faEdit, faTrashAlt } from '@fortawesome/pro-light-svg-icons';
import { YesNoDialog } from '@/components/dialogs';
import {
    ConfiguratorBackendDto,
    useConfiguratorBackends,
    useDeleteConfiguratorBackend,
} from '@/services/configurator-backends';
import { useRoomMap } from '@/services/rooms';
import { ConfiguratorBackendDialog } from './ConfiguratorBackendDialog';
import theme from './Configurators3DPage.module.scss';

interface Configurators3DPageProps {
    projectId: number;
}

export const Configurators3DPage: React.FC<Configurators3DPageProps> = ({ projectId }) => {
    const { data: backends, isLoading } = useConfiguratorBackends(projectId);
    const roomMap = useRoomMap(projectId);
    const deleteMutation = useDeleteConfiguratorBackend(projectId);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState<ConfiguratorBackendDto | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<ConfiguratorBackendDto | null>(null);

    const openCreate = () => {
        setEditing(null);
        setDialogOpen(true);
    };

    const openEdit = (backend: ConfiguratorBackendDto) => {
        setEditing(backend);
        setDialogOpen(true);
    };

    const confirmDelete = async () => {
        if (deleteTarget) {
            await deleteMutation.mutateAsync(deleteTarget.id);
        }
        setDeleteTarget(null);
    };

    return (
        <div className={theme.container}>
            <div className={theme.header}>
                <Button variant="primary" icon={faPlus} onClick={openCreate}>
                    <Trans>Add 3D configurator</Trans>
                </Button>
            </div>

            {isLoading ? (
                <LoadingIndicator />
            ) : !backends || backends.length === 0 ? (
                <p className={theme.empty}>
                    <Trans>No 3D configurators yet.</Trans>
                </p>
            ) : (
                <div className={theme.grid}>
                    {backends.map((backend) => (
                        <SimpleCard key={backend.id} className={theme.card}>
                            <div className={theme.preview}>
                                {backend.previewImageUrl ? (
                                    <img
                                        className={theme.previewImage}
                                        src={backend.previewImageUrl}
                                        alt={t`Preview of ${backend.name}`}
                                        loading="lazy"
                                    />
                                ) : (
                                    <div className={theme.previewPlaceholder}>
                                        <FontAwesomeIcon icon={faCube} size="2x" />
                                    </div>
                                )}
                            </div>
                            <div className={theme.body}>
                                <div className={theme.titleRow}>
                                    <div className={theme.titleMain}>
                                        <div className={theme.name}>{backend.name}</div>
                                        <div className={theme.tags}>
                                            {backend.rooms.length === 0 ? (
                                                <span className={theme.noRooms}>
                                                    <Trans>No rooms assigned</Trans>
                                                </span>
                                            ) : (
                                                backend.rooms.map((room) => {
                                                    const roomInfo = roomMap?.get(room.projectTagId);
                                                    const iconProp: IconProp | undefined = roomInfo?.icon
                                                        ? (['fal', roomInfo.icon] as IconProp)
                                                        : undefined;
                                                    return (
                                                        <Badge
                                                            key={room.projectTagId}
                                                            variant="info"
                                                            appearance="subtle"
                                                            size="sm"
                                                            iconLeft={iconProp}>
                                                            {roomInfo?.name ?? room.roomValue}
                                                        </Badge>
                                                    );
                                                })
                                            )}
                                        </div>
                                    </div>
                                    <DropdownContextMenu>
                                        <Dropdown.Item icon={faEdit} onClick={() => openEdit(backend)}>
                                            <Trans>Edit</Trans>
                                        </Dropdown.Item>
                                        <Dropdown.Item icon={faTrashAlt} onClick={() => setDeleteTarget(backend)}>
                                            <Trans>Delete</Trans>
                                        </Dropdown.Item>
                                    </DropdownContextMenu>
                                </div>
                            </div>
                        </SimpleCard>
                    ))}
                </div>
            )}

            <ConfiguratorBackendDialog
                projectId={projectId}
                backend={editing}
                show={dialogOpen}
                onClose={() => setDialogOpen(false)}
            />

            <YesNoDialog
                show={!!deleteTarget}
                onClose={() => setDeleteTarget(null)}
                onAccept={confirmDelete}
                confirmButtonColor="danger"
                confirmButtonText={<Trans>Delete</Trans>}
                cancelButtonText={<Trans>Cancel</Trans>}
                title={<Trans>Delete 3D configurator</Trans>}
                body={<Trans>Are you sure you want to delete this configurator?</Trans>}
            />
        </div>
    );
};
